import { useState } from "react";
import { Link } from "wouter";
import { Activity, Stethoscope, Heart, ArrowRight, Calculator } from "lucide-react";
import { LegalFooter } from "@/components/layout/legal-footer";

export default function Landing() {
  const [active, setActive] = useState<"hcp" | "patient" | null>(null);
  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");

  const bgColor =
    active === "hcp" ? "#002470" : active === "patient" ? "#fff9f7" : "#f8fafc";

  const logoIconColor = active === "hcp" ? "#7bb8ff" : "#0f766e";
  const logoTextColor = active === "hcp" ? "#ffffff" : "#0f172a";
  const navTextColor  = active === "hcp" ? "rgba(255,255,255,0.80)" : "#475569";

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: bgColor, transition: "background 0.4s ease", fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between sticky top-0 z-10" style={{ borderBottom: active === "hcp" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0", background: "inherit", transition: "border-color 0.4s ease" }}>
        <div className="flex items-center gap-2">
          <Activity className="h-6 w-6 transition-colors duration-300" style={{ color: logoIconColor }} aria-hidden="true" />
          <div className="flex flex-col leading-none">
            <span className="text-xl font-bold tracking-tight transition-colors duration-300" style={{ color: logoTextColor }}>
              {import.meta.env.VITE_APP_NAME || "RheumHub"}
            </span>
            <span className="text-[10px] font-medium tracking-wide transition-colors duration-300" style={{ color: active === "hcp" ? "rgba(255,255,255,0.70)" : "#64748b" }}>
              by Medantix Health Ltd
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link
            href="/sign-in"
            className="text-sm font-medium transition-colors duration-300"
            style={{ color: navTextColor }}
            data-testid="link-sign-in"
          >
            Log in
          </Link>
          <Link href="/sign-up" data-testid="link-sign-up">
            <button
              className="text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-300"
              style={
                active === "hcp"
                  ? { background: "#7bb8ff", color: "#002470" }
                  : { background: "#0f766e", color: "#ffffff" }
              }
            >
              Get Started
            </button>
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-10">

        {/*
          Single hover zone: headline + tiles share one onMouseLeave so moving
          through the gap between tiles or between tiles and the clinical tools
          strip never re-triggers a state toggle mid-animation.
          onMouseEnter is per-column; onMouseLeave is only on the outer wrapper.
        */}
        <div
          className="w-full max-w-lg"
          onMouseLeave={() => setActive(null)}
        >
          {/* Fixed-height headline — absolute inner content so height never shifts */}
          <div className="text-center mb-10 relative" style={{ height: 220 }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center">

              {/* Neutral */}
              <div
                className="flex flex-col items-center transition-opacity duration-300 absolute inset-0 justify-center"
                style={{ opacity: active === null ? 1 : 0, pointerEvents: active === null ? "auto" : "none" }}
                aria-hidden={active !== null}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-800 text-sm font-medium mb-5 border border-teal-100">
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" />
                  </span>
                  The UK's dedicated rheumatology platform
                </div>
                <h1 className="text-5xl font-bold text-slate-900 tracking-tight mb-3 leading-tight">
                  You're here<br />for a reason.
                </h1>
                <p className="text-base text-slate-500 max-w-sm mx-auto leading-relaxed">
                  Tell us who you are and we'll show you exactly what you need.
                </p>
              </div>

              {/* HCP */}
              <div
                className="flex flex-col items-center transition-opacity duration-300 absolute inset-0 justify-center"
                style={{ opacity: active === "hcp" ? 1 : 0, pointerEvents: active === "hcp" ? "auto" : "none" }}
                aria-hidden={active !== "hcp"}
              >
                <p className="text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: "#7bb8ff" }}>
                  Healthcare Professional
                </p>
                <h1 className="text-5xl font-bold tracking-tight mb-3 leading-tight" style={{ color: "#ffffff" }}>
                  Clinical precision,<br />at your fingertips.
                </h1>
                <p className="text-base max-w-xs mx-auto leading-relaxed" style={{ color: "rgba(255,255,255,0.80)" }}>
                  NICE, BSR & EULAR guidelines. Drug reference. Scenarios. AI assistant.
                </p>
              </div>

              {/* Patient */}
              <div
                className="flex flex-col items-center transition-opacity duration-300 absolute inset-0 justify-center"
                style={{ opacity: active === "patient" ? 1 : 0, pointerEvents: active === "patient" ? "auto" : "none" }}
                aria-hidden={active !== "patient"}
              >
                <p className="text-xs font-bold uppercase tracking-[0.18em] mb-3 text-rose-500">
                  Patient
                </p>
                <h1 className="text-5xl font-bold text-slate-900 tracking-tight mb-3 leading-tight">
                  Understanding<br />your condition.
                </h1>
                <p className="text-slate-500 text-base max-w-xs mx-auto leading-relaxed">
                  Plain language guides, trusted NHS resources, and a warm AI companion.
                </p>
              </div>

            </div>
          </div>

          {/* Identity tiles */}
          <div className="flex gap-4">
            {/* HCP column — onMouseEnter only, no onMouseLeave */}
            <div
              className="flex-1 flex flex-col gap-2"
              onMouseEnter={() => setActive("hcp")}
            >
              <div
                className="text-left rounded-2xl p-6 border-2 transition-all duration-300 cursor-pointer"
                style={
                  active === "hcp"
                    ? { background: "rgba(123,184,255,0.15)", borderColor: "#7bb8ff" }
                    : active === "patient"
                    ? { background: "rgba(0,0,0,0.15)", borderColor: "rgba(255,255,255,0.08)" }
                    : { background: "white", borderColor: "#e2e8f0" }
                }
              >
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300"
                  style={ active === "hcp" ? { background: "rgba(123,184,255,0.2)" } : { background: "#f0fdf4" } }
                >
                  <Stethoscope className="h-5 w-5 transition-colors duration-300" style={{ color: active === "hcp" ? "#7bb8ff" : "#0f766e" }} aria-hidden="true" />
                </div>
                <p
                  className="font-bold text-base mb-1 transition-colors duration-300"
                  style={{ color: active === "hcp" ? "white" : "#0f172a" }}
                >
                  I'm an HCP
                </p>
                <p
                  className="text-xs leading-relaxed transition-colors duration-300"
                  style={{ color: active === "hcp" ? "rgba(255,255,255,0.75)" : active === "patient" ? "rgba(255,255,255,0.25)" : "#64748b" }}
                >
                  Nurse, doctor, pharmacist,<br />physio or OT
                </p>
              </div>
              <div
                className="transition-opacity duration-300"
                style={{ opacity: active === "hcp" ? 1 : 0, pointerEvents: active === "hcp" ? "auto" : "none" }}
                aria-hidden={active !== "hcp"}
                inert={active !== "hcp" ? true : undefined}
              >
                <Link href="/sign-up" data-testid="link-nurse-signup">
                  <button
                    className="w-full flex items-center justify-between px-6 py-3.5 rounded-2xl font-semibold text-sm"
                    style={{ background: "#7bb8ff", color: "#002470" }}
                  >
                    Create your HCP account
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </Link>
              </div>
            </div>

            {/* Patient column — only shown on advicelinehub.co.uk */}
            {isAdvicelineHub && (
            <div
              className="flex-1 flex flex-col gap-2"
              onMouseEnter={() => setActive("patient")}
            >
              <div
                className="text-left rounded-2xl p-6 border-2 transition-all duration-300 cursor-pointer"
                style={
                  active === "patient"
                    ? { background: "white", borderColor: "#fda4af" }
                    : active === "hcp"
                    ? { background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.08)" }
                    : { background: "white", borderColor: "#e2e8f0" }
                }
              >
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center mb-4"
                  style={ active === "patient" ? { background: "#ffe4e6" } : { background: "#fff1f2" } }
                >
                  <Heart className="h-5 w-5 text-rose-500" aria-hidden="true" />
                </div>
                <p
                  className="font-bold text-base mb-1 transition-colors duration-300"
                  style={{ color: active === "patient" ? "#0f172a" : active === "hcp" ? "rgba(255,255,255,0.35)" : "#0f172a" }}
                >
                  I'm a Patient
                </p>
                <p
                  className="text-xs leading-relaxed transition-colors duration-300"
                  style={{ color: active === "patient" ? "#64748b" : active === "hcp" ? "rgba(255,255,255,0.25)" : "#64748b" }}
                >
                  Living with a rheumatic condition<br />or recently diagnosed
                </p>
              </div>
              <div
                className="transition-opacity duration-300"
                style={{ opacity: active === "patient" ? 1 : 0, pointerEvents: active === "patient" ? "auto" : "none" }}
                aria-hidden={active !== "patient"}
                inert={active !== "patient" ? true : undefined}
              >
                <Link href="/sign-up" data-testid="link-patient-signup">
                  <button className="w-full flex items-center justify-between bg-rose-500 hover:bg-rose-600 text-white px-6 py-3.5 rounded-2xl font-semibold text-sm transition-colors">
                    Create your patient account
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </Link>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Clinical tools strip */}
        <Link href="/clinical-tools" data-testid="link-clinical-tools-banner" className="w-full max-w-lg mt-6 block">
          <div className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 cursor-pointer bg-teal-700 hover:bg-teal-800 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-teal-600 flex items-center justify-center shrink-0" aria-hidden="true">
                <Calculator className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-xs">Free Clinical Tools — no account needed</p>
                <p className="text-teal-50 text-xs">DAS28, SLEDAI-2K, BASDAI, mHAQ, CDAI, SDAI + drug dosage calculators</p>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-teal-100 shrink-0" aria-hidden="true" />
          </div>
        </Link>
      </main>

      <LegalFooter colorScheme={active === "hcp" ? "dark" : "light"} />
    </div>
  );
}
